import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Mail, Phone } from 'lucide-react';
import { CLOUD_FUNCTIONS } from '../../auth/FirebaseContext';
import { useLoginFlow } from '../../auth/LoginProvider';
import { brandConfig } from '../../config';
import { cloud_sendSignInLinkEmail } from '../../utils/owner-portal/cloudFunctions';

// ----------------------------------------------------------------------

type EmailSentConfirmationProps = {
  back: () => void;
};

export default function EmailSentConfirmation({ back }: EmailSentConfirmationProps) {
  const { emailToVerify, phoneToVerify } = useLoginFlow();
  const [codeResent, setCodeResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendingLink, setResendingLink] = useState(false);

  // Show the credential that was actually used (email is preferred for Owner Portal)
  const credentialToShow = emailToVerify || phoneToVerify;

  const handleResendCode = async () => {
    setResendingLink(true);
    setCodeResent(false);
    setResendError(null);

    try {
      const payload = { email: credentialToShow, brand: brandConfig.brandCode };
      const response = await httpsCallable(CLOUD_FUNCTIONS, cloud_sendSignInLinkEmail)(payload);

      const data = response.data as Record<string, unknown>;

      if (!data || !data.email) {
        console.error('Invalid response data - missing email');
        setResendError('There was a problem with this email. Please try again.');
        setResendingLink(false);
        return;
      }
      setResendingLink(false);
      setCodeResent(true);
    } catch (caught: unknown) {
      const err = caught as { code?: string; message?: string };
      console.error('Resend failed with error:', caught);

      setResendingLink(false);
      // Check for custom domain not supported error
      if (
        err?.code === 'functions/failed-precondition' &&
        err?.message?.includes('Email links not supported')
      ) {
        setResendError(err.message);
      } else {
        setResendError('There was a problem sending the email link. Please try again.');
      }
    }
  };

  const loading = resendingLink;
  const isEmail = Boolean(emailToVerify);

  return (
    <div className="flex flex-col gap-6 text-foreground">
      <div className="flex gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary dark:text-secondary">
          {isEmail ? (
            <Mail className="h-5 w-5" aria-hidden />
          ) : (
            <Phone className="h-5 w-5" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-base font-semibold leading-snug text-foreground">
            {isEmail
              ? 'A login link has been sent to your email.'
              : 'A code has been sent to your phone.'}
          </p>
          <p className="text-sm text-muted-foreground">
            {isEmail
              ? 'Please follow that link to sign in.'
              : 'Enter the code when prompted to sign in.'}
          </p>
        </div>
      </div>

      <div className="flex flex-col items-center">
        <span>
          {isEmail ? "Didn't get an email?" : "Didn't get a code?"}{' '}
          <button
            type="button"
            onClick={async (e) => {
              e.preventDefault();
              if (loading) {
                return;
              }
              await handleResendCode();
            }}
            disabled={loading}
            className="font-medium text-primary dark:text-secondary underline-offset-4 hover:underline disabled:text-muted-foreground"
          >
            Resend
          </button>
        </span>
        {resendError && <p className="text-sm text-destructive">{resendError}</p>}
        {codeResent && (
          <p className="text-sm text-muted-foreground">
            {isEmail ? 'Login email resent' : 'Code resent'}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={back}
        className="w-fit font-medium text-primary dark:text-secondary underline-offset-4 hover:underline"
      >
        Back
      </button>
    </div>
  );
}
